"use client";

/**
 * Calendar — react-day-picker v10 래퍼 (shadcn/ui 스타일, 한국어)
 *
 * 헤더: [이전] [연도 드롭다운] [월 드롭다운] [다음] — 겹침 없이 한 줄로 배치.
 * AdminDatePicker 내부에서 사용하며, 직접 사용도 가능하다.
 *
 * @example
 * <Calendar mode="single" selected={date} onSelect={setDate} />
 *
 * @example // 선택 가능 연도 범위 지정
 * <Calendar mode="single" selected={date} onSelect={setDate}
 *   startMonth={new Date(1950, 0)} endMonth={new Date(2010, 11)} />
 */
import { createContext, useContext } from "react";
import type { MonthCaptionProps } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, useDayPicker } from "react-day-picker";
import { ko } from "react-day-picker/locale";

import { cn } from "../../lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const TODAY = new Date();

const MONTHS_KO = [
    "1월", "2월", "3월", "4월", "5월", "6월",
    "7월", "8월", "9월", "10월", "11월", "12월",
];

const selectCls =
    "h-8 cursor-pointer rounded-md border border-input bg-transparent px-2 text-sm font-medium " +
    "outline-none hover:bg-accent focus:border-ring focus:ring-[3px] focus:ring-ring/50";

const navBtnCls =
    "inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-input bg-transparent " +
    "text-muted-foreground shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground " +
    "disabled:pointer-events-none disabled:opacity-40";

/** 연도 범위를 DropdownCaption 으로 전달하기 위한 내부 context */
const YearRangeCtx = createContext({
    startYear: TODAY.getFullYear() - 100,
    endYear: TODAY.getFullYear() + 10,
});

/**
 * 헤더를 통째로 교체한 커스텀 MonthCaption.
 * prev 버튼 · 연도 select · 월 select · next 버튼을 한 flex 행에 배치해
 * absolute 포지션 없이도 겹침이 생기지 않는다.
 */
function DropdownCaption({ calendarMonth }: MonthCaptionProps) {
    const { startYear, endYear } = useContext(YearRangeCtx);
    const { goToMonth, previousMonth, nextMonth } = useDayPicker();

    const date = calendarMonth.date;
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-indexed

    const years = Array.from(
        { length: endYear - startYear + 1 },
        (_, i) => startYear + i,
    );

    return (
        <div className="flex w-full items-center justify-between gap-1">
            {/* 이전 달 */}
            <button
                type="button"
                aria-label="이전 달"
                disabled={!previousMonth}
                onClick={() => previousMonth && goToMonth(previousMonth)}
                className={navBtnCls}
            >
                <ChevronLeft className="size-4" />
            </button>

            {/* 연도 · 월 드롭다운 */}
            <div className="flex items-center gap-1.5">
                <select
                    value={year}
                    onChange={(e) =>
                        goToMonth(new Date(Number(e.target.value), month))
                    }
                    className={selectCls}
                >
                    {years.map((y) => (
                        <option key={y} value={y}>{y}년</option>
                    ))}
                </select>
                <select
                    value={month}
                    onChange={(e) =>
                        goToMonth(new Date(year, Number(e.target.value)))
                    }
                    className={selectCls}
                >
                    {MONTHS_KO.map((label, i) => (
                        <option key={i} value={i}>{label}</option>
                    ))}
                </select>
            </div>

            {/* 다음 달 */}
            <button
                type="button"
                aria-label="다음 달"
                disabled={!nextMonth}
                onClick={() => nextMonth && goToMonth(nextMonth)}
                className={navBtnCls}
            >
                <ChevronRight className="size-4" />
            </button>
        </div>
    );
}

export function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    startMonth,
    endMonth,
    ...props
}: CalendarProps) {
    const startYear = (startMonth ?? new Date(TODAY.getFullYear() - 100, 0)).getFullYear();
    const endYear = (endMonth ?? new Date(TODAY.getFullYear() + 10, 11)).getFullYear();

    return (
        <YearRangeCtx.Provider value={{ startYear, endYear }}>
            <DayPicker
                locale={ko}
                showOutsideDays={showOutsideDays}
                {...(startMonth ? { startMonth } : {})}
                {...(endMonth ? { endMonth } : {})}
                className={cn("p-3", className)}
                classNames={{
                    months: "flex flex-col gap-4",
                    month: "flex flex-col gap-4",
                    /* 캡션 행: DropdownCaption 이 full-width flex 행을 담당 */
                    month_caption: "flex h-9 items-center",
                    caption_label: "hidden",
                    /* 원본 nav 는 DropdownCaption 안에서 처리하므로 숨김 */
                    nav: "hidden",
                    /* 날짜 그리드 */
                    month_grid: "w-full border-collapse",
                    weekdays: "flex",
                    weekday:
                        "w-8 text-center text-[0.75rem] font-normal text-muted-foreground",
                    week: "mt-2 flex",
                    /* 날짜 셀·버튼 */
                    day: "h-8 w-8 p-0 text-center text-sm",
                    day_button: cn(
                        "flex size-8 items-center justify-center rounded-md font-normal transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                        "disabled:pointer-events-none disabled:opacity-30",
                    ),
                    /* 상태 수식 클래스 */
                    selected:
                        "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                    today: "bg-accent text-accent-foreground",
                    outside: "text-muted-foreground opacity-40",
                    disabled: "pointer-events-none opacity-30",
                    range_start: "rounded-l-md",
                    range_end: "rounded-r-md",
                    range_middle:
                        "rounded-none bg-accent text-accent-foreground",
                    hidden: "invisible",
                    ...classNames,
                }}
                components={{
                    /* 원본 Chevron 은 DropdownCaption 에서 직접 쓰므로 빈 컴포넌트 */
                    MonthCaption: DropdownCaption,
                }}
                {...props}
            />
        </YearRangeCtx.Provider>
    );
}
